import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {ActivityIndicator} from 'trpc-react-native-ui';
import {getDate, setDate, subMonths} from 'date-fns';

import {dateUtils} from '../../../lib/date-utils';
import UsageChart from './UsageChart';
import TimeChartLabels from './TimeChartLabels';
import MaxUsageChartLabel from './MaxUsageChartLabel';
import UsageChartPin from './UsageChartPin';
import {max as _max} from 'lodash';
import I18n from '../../../lib/i18n';

const CHART_HEIGHT = 150;

function getStyle() {
  return StyleSheet.create({
    container: {
      marginBottom: 20,
    },
    maxLabel: {
      position: 'absolute',
      left: 50,
      top: 10,
      fontSize: 13,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    currentPin: {
      position: 'absolute',
      backgroundColor: 'transparent',
    },
    containerChart: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'white',
    },
    chartChart: {
      width: 200,
      height: 200,
    },
  });
}

function filterPastLabels(labels) {
  return labels.filter((value, index) => new Date(labels[index]) < new Date());
}

function filterPastValues(values, labels) {
  return values.filter((value, index) => new Date(labels[index]) < new Date());
}

function formatValue(value, unit) {
  switch (unit) {
    case 'DURATION':
      return value / 60;
    case 'VOLUME':
      return value / (1024 * 1024);
    default:
      return value;
  }
}

function formatValues(values, unit) {
  return values.map(value => formatValue(value, unit));
}

class DecoratedUsageChart extends Component {
  constructor(props) {
    super(props);
    this.graphHeight = CHART_HEIGHT;
    this.state = {
      graphWidth: 0,
      graphHeight: 0,
    };
    this.onLayout = this.onLayout.bind(this);
  }

  componentDidMount() {
    if (this.props.fetchUsageAggregate) {
      const displayMode =
        this.props.displayMode === undefined ? 'daily' : this.props.displayMode;
      this.fetchData(displayMode);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const displayMode =
      this.props.displayMode === undefined ? 'daily' : this.props.displayMode;
    if (
      this.props.fetchUsageAggregate &&
      nextProps.displayMode !== displayMode
    ) {
      this.fetchData(nextProps.displayMode);
    }
  }

  onLayout(event) {
    const {width, height} = event.nativeEvent.layout;
    this.setState({graphWidth: width, graphHeight: height});
  }

  getDailyDateRange() {
    // Keep the same day of month to identify the previous renewal date
    const currentDayOfMonth = getDate(this.props.nextRenewalDate);
    const beginDate = dateUtils.format(
      setDate(subMonths(this.props.nextRenewalDate, 1), currentDayOfMonth),
      'yyyyMMddHHmmss',
    );
    const endDate = dateUtils.format(
      this.props.nextRenewalDate,
      'yyyyMMddHHmmss',
    );
    return {
      beginDate,
      endDate,
    };
  }

  getMonthlyDateRange() {
    const beginDate = dateUtils.format(
      subMonths(this.props.nextRenewalDate, 13),
      'yyyyMMdd',
    );
    const endDate = dateUtils.format(
      subMonths(this.props.nextRenewalDate, 1),
      'yyyyMMdd',
    );
    return {
      beginDate,
      endDate,
    };
  }

  getParsedStringDate(stringDate) {
    // Incoming stringDate has format yyyy/MM/dd or yyyy/dd/MM depending on locale
    const reversedStringDate = stringDate.split('-').reverse().join('-');
    return dateUtils.parse(reversedStringDate, 'dd-MM-yyyy');
  }

  getDisplayedLabel() {
    const displayMode =
      this.props.displayMode === undefined ? 'daily' : this.props.displayMode;
    const labels = this.props.data.labels;
    if (displayMode === 'daily') {
      const dateFormat = 'MMM Do';
      const firstLabelDate =
        typeof labels[0] === 'string'
          ? this.getParsedStringDate(labels[0])
          : labels[0];
      const lastLabelDate =
        typeof labels[labels.length - 1] === 'string'
          ? this.getParsedStringDate(labels[labels.length - 1])
          : labels[labels.length - 1];
      return [
        dateUtils.format(firstLabelDate, dateFormat),
        dateUtils.format(lastLabelDate, dateFormat),
      ];
    }

    const firstLabel = I18n.t(`USAGE_LEGEND.${this.props.type}`);
    const lastLabelDate =
      typeof labels[labels.length - 1] === 'string'
        ? this.getParsedStringDate(labels[labels.length - 1])
        : labels[labels.length - 1];
    return [firstLabel, dateUtils.format(lastLabelDate, 'MMM yyyy')];
  }

  shouldDisplayPin(currentValue) {
    const displayMode =
      this.props.displayMode === undefined ? 'daily' : this.props.displayMode;
    return currentValue && displayMode === 'daily';
  }

  shouldDisplayMax() {
    const displayMode =
      this.props.displayMode === undefined ? 'daily' : this.props.displayMode;
    return displayMode === 'daily';
  }

  computeCurrentPinPosition(points, labels, currentValue, max) {
    const minXPosition = 0;
    const maxXPosition = this.state.graphWidth;
    const currentXValue =
      1 - (labels.length - filterPastLabels(labels).length) / labels.length;
    this.currentLabelXPosition = currentXValue * (maxXPosition - minXPosition);
    // Depends on the libraries which differ between platforms
    const graphPaddingTop = Platform.OS === 'ios' ? 30 : 32;
    const graphPaddingBottom = Platform.OS === 'ios' ? 15 : 28;
    const highestValue = isNaN(max) ? _max(points) : max;
    this.currentLabelYPosition =
      ((this.state.graphHeight - graphPaddingTop - graphPaddingBottom) /
        highestValue) *
        currentValue +
      graphPaddingBottom;
  }

  fetchData(displayMode) {
    var period = displayMode === 'monthly' ? 'MONTH' : 'DAY';
    const {beginDate, endDate} =
      displayMode === 'monthly'
        ? this.getMonthlyDateRange()
        : this.getDailyDateRange();
    this.props.fetchUsageAggregate(null, period, beginDate, endDate);
  }

  render() {
    const styles = getStyle();
    const {data, unitType, color, displayMode = 'daily'} = this.props;
    if (!data) {
      return <ActivityIndicator />;
    }
    const {points, labels} = data;
    const max = this.props.max;

    const filteredValues = filterPastValues(points, labels);
    const formatterValues = formatValues(filteredValues, unitType);
    const currentValue = filteredValues[filteredValues.length - 1];
    this.computeCurrentPinPosition(points, labels, currentValue, max);

    const displayedLabel = this.getDisplayedLabel();
    return this.props.isFetching ? (
      <ActivityIndicator style={{height: CHART_HEIGHT}} />
    ) : (
      <View style={styles.container}>
        <UsageChart
          data={{points: formatterValues, labels: data.labels}}
          max={this.shouldDisplayMax() ? formatValue(max, unitType) : null}
          unitType={unitType}
          color={color}
          onLayout={this.onLayout}
          chartType={displayMode === 'daily' ? 'line' : 'bar'}
        />
        <TimeChartLabels labels={displayedLabel} />
        {this.shouldDisplayMax() ? (
          <MaxUsageChartLabel
            unitType={unitType}
            value={max}
            color={color}
            style={styles.maxLabel}
          />
        ) : null}
        {this.shouldDisplayPin(currentValue) ? (
          <UsageChartPin
            value={currentValue}
            unitType={unitType}
            color={color}
            reversed={filterPastLabels(labels).length > labels.length / 2}
            style={[
              styles.currentPin,
              {
                bottom: this.currentLabelYPosition,
                left: this.currentLabelXPosition,
              },
            ]}
          />
        ) : null}
      </View>
    );
  }
}

DecoratedUsageChart.propTypes = {
  data: PropTypes.shape({
    points: PropTypes.arrayOf(PropTypes.number),
    labels: PropTypes.arrayOf(PropTypes.string),
  }),
  color: PropTypes.string.isRequired,
  unitType: PropTypes.string,
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  displayMode: PropTypes.oneOf(['daily', 'monthly']).isRequired,
  isFetching: PropTypes.bool,
  nextRenewalDate: PropTypes.number,
  fetchUsageAggregate: PropTypes.func,
  type: PropTypes.string,
};

export default DecoratedUsageChart;
